package com.nonopn.skydome.common.app.checkout

import com.nonopn.skydome.common.app.api.impl.AccountStatus
import com.nonopn.skydome.common.app.api.impl.ProbeSource
import com.nonopn.skydome.common.app.api.impl.SubscriptionPublicState
import com.nonopn.skywatcher.common.api.CheckoutLoginRequest
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock

// Return from the Stripe Checkout on the web app (contract of 2026-11-05):
//   success: /?checkout=success&product=<product>&session_id=<id>&code=<one time login code>
//   cancel:  /?checkout=cancel&product=<product>  (no code: no login attempt)

const val CheckoutApp = "StrikeRadar"

private const val ParamCheckout = "checkout"
private const val ParamProduct = "product"
private const val ParamSessionId = "session_id"
private const val ParamCode = "code"
private val CheckoutParams = setOf(ParamCheckout, ParamProduct, ParamSessionId, ParamCode)

sealed interface CheckoutReturn {
    val product: String?

    data class Success(
        override val product: String?,
        val sessionId: String?,
        val code: String?
    ) : CheckoutReturn

    data class Cancel(override val product: String?) : CheckoutReturn
}

/** Reads the query string of the launch URL ("?a=b&c=d" or "a=b&c=d"). Null when it is not a checkout return. */
fun parseCheckoutReturn(query: String): CheckoutReturn? {
    val params = parseQuery(query)
    val product = params[ParamProduct]?.takeIf { it.isNotBlank() }
    return when (params[ParamCheckout]?.trim()?.lowercase()) {
        "success" -> CheckoutReturn.Success(
            product = product,
            sessionId = params[ParamSessionId]?.trim()?.takeIf { it.isNotEmpty() },
            code = params[ParamCode]?.trim()?.takeIf { it.isNotEmpty() }
        )

        "cancel", "canceled", "cancelled" -> CheckoutReturn.Cancel(product)
        else -> null
    }
}

/** Body of POST /api/checkout/login, or null when the URL lacks the code or the session id (no call then). */
fun CheckoutReturn.Success.loginRequest(): CheckoutLoginRequest? {
    val code = code ?: return null
    val sessionId = sessionId ?: return null
    return CheckoutLoginRequest(app = CheckoutApp, code = code, sessionId = sessionId)
}

/** The query string without the checkout parameters (other parameters kept as they were): "" or "?…". */
fun queryWithoutCheckoutParams(query: String): String {
    val kept = query.removePrefix("?")
        .split('&')
        .filter { it.isNotEmpty() }
        .filterNot { decodeQueryComponent(it.substringBefore('=')) in CheckoutParams }
    return if (kept.isEmpty()) "" else kept.joinToString("&", prefix = "?")
}

internal fun parseQuery(query: String): Map<String, String> {
    val result = LinkedHashMap<String, String>()
    query.removePrefix("?").split('&').filter { it.isNotEmpty() }.forEach { pair ->
        val key = decodeQueryComponent(pair.substringBefore('='))
        val value = if (pair.contains('=')) decodeQueryComponent(pair.substringAfter('=')) else ""
        // First occurrence wins
        if (key.isNotEmpty() && key !in result) result[key] = value
    }
    return result
}

private const val HexRadix = 16
private const val PercentEncodedLength = 3

/** application/x-www-form-urlencoded decoding ('+' = space, %XX = UTF-8 byte). Invalid sequences are kept. */
internal fun decodeQueryComponent(raw: String): String {
    if ('%' !in raw && '+' !in raw) return raw
    val bytes = ArrayList<Byte>(raw.length)
    var i = 0
    while (i < raw.length) {
        val c = raw[i]
        val hex = if (c == '%' && i + 2 < raw.length && raw[i + 1].isHexDigit() && raw[i + 2].isHexDigit()) {
            raw.substring(i + 1, i + PercentEncodedLength).toInt(HexRadix)
        } else {
            null
        }
        when {
            hex != null -> {
                bytes.add(hex.toByte())
                i += PercentEncodedLength
            }

            c == '+' -> {
                bytes.add(' '.code.toByte())
                i++
            }

            else -> {
                c.toString().encodeToByteArray().forEach { bytes.add(it) }
                i++
            }
        }
    }
    return bytes.toByteArray().decodeToString()
}

private fun Char.isHexDigit() = this in '0'..'9' || this in 'a'..'f' || this in 'A'..'F'

/**
 * Runs the checkout login once per code: the code is single use, a second call would answer InvalidLoginCode
 * and send a customer who just paid back to the login screen. Repeated calls (recomposition, second
 * initialisation) get the result of the first one; a code already attempted in this browser tab (reload while
 * the first call was running) is not sent again and gives [alreadyAttempted].
 */
class CheckoutLoginGuard<R>(
    private val store: CheckoutAttemptStore,
    private val alreadyAttempted: R
) {
    private val mutex = Mutex()
    private val results = mutableMapOf<String, CompletableDeferred<R>>()

    @Suppress("TooGenericExceptionCaught")
    suspend fun runOnce(code: String, call: suspend () -> R): R {
        var owner = false
        val deferred = mutex.withLock {
            results.getOrPut(code) {
                CompletableDeferred<R>().also { created ->
                    if (store.wasAttempted(code)) {
                        created.complete(alreadyAttempted)
                    } else {
                        store.markAttempted(code)
                        owner = true
                    }
                }
            }
        }

        if (owner) {
            try {
                deferred.complete(call())
            } catch (e: CancellationException) {
                deferred.complete(alreadyAttempted)
                throw e
            } catch (e: Throwable) {
                deferred.completeExceptionally(e)
            }
        }

        return deferred.await()
    }
}

/** Remembers the codes already sent (sessionStorage on the web, memory elsewhere). */
interface CheckoutAttemptStore {
    fun wasAttempted(code: String): Boolean
    fun markAttempted(code: String)
}

class MemoryCheckoutAttemptStore : CheckoutAttemptStore {
    private val codes = mutableSetOf<String>()
    override fun wasAttempted(code: String) = code in codes
    override fun markAttempted(code: String) {
        codes.add(code)
    }
}

/** Polling of GET /api/account/status after the payment, while the Stripe webhook activates the purchase. */
object CheckoutActivationPolling {
    const val IntervalMillis = 3_000L
    const val MaxDurationMillis = 120_000L
    val MaxAttempts = (MaxDurationMillis / IntervalMillis).toInt()
}

/**
 * True once the purchase is visible: live subscription or active paid probe with no unpaid probe left, or Nsg
 * credits above the first value seen after the return ([baselineCredits], null on the first status).
 */
fun isCheckoutActivated(status: AccountStatus, baselineCredits: Int?): Boolean {
    val subscriptionLive = when (status.subscription?.publicState()) {
        SubscriptionPublicState.Active, SubscriptionPublicState.Trialing -> true
        else -> false
    }
    val paidProbeActive = status.probes.any {
        it.status.trim().equals("Active", ignoreCase = true) &&
            !it.isDemo &&
            it.probeSource() == ProbeSource.Subscription
    }
    val credits = status.strikeRadar?.nsgCredits
    val creditsAdded = baselineCredits != null && credits != null && credits > baselineCredits
    return (status.unpaidProbes == 0 && (subscriptionLive || paidProbeActive)) || creditsAdded
}
